export interface ActiveTrigger {
  trigger: '@' | '#';
  query: string;
  startNode?: Text;
  startOffset?: number;
  unresolvedElement?: HTMLElement;
}

const isTextNode = (node: Node): node is Text =>
  node.nodeType === Node.TEXT_NODE;

const isWhitespaceChar = (char: string): boolean =>
  char === ' ' || char === '\u00a0' || char === '\n';

const isTriggerChar = (char: string): char is '@' | '#' =>
  char === '@' || char === '#';

interface TriggerMatch {
  index: number;
  trigger: '@' | '#';
}

// Spaces are allowed in the query so multi-word names (e.g. "AI in
// healthcare") can be searched. A newline still closes the trigger.
const scanForTriggerIndex = (before: string): TriggerMatch | null => {
  for (let i = before.length - 1; i >= 0; i--) {
    const char = before[i];
    if (char === '\n') {
      return null;
    }
    if (!isTriggerChar(char)) {
      continue;
    }
    const prev = i === 0 ? ' ' : before[i - 1];
    return i === 0 || isWhitespaceChar(prev)
      ? { index: i, trigger: char }
      : null;
  }
  return null;
};

/** Trailing spaces are not part of the searchable / wrap token. */
export const trimMentionQuery = (query: string): string =>
  query.replace(/[\s\u00a0]+$/g, '');

export const findActiveTrigger = (root: HTMLElement): ActiveTrigger | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  if (!range.collapsed || !root.contains(range.startContainer)) {
    return null;
  }

  const node = range.startContainer;
  if (!isTextNode(node)) {
    return null;
  }

  const before = (node.nodeValue ?? '').slice(0, range.startOffset);
  const match = scanForTriggerIndex(before);
  if (!match) {
    return null;
  }

  return {
    trigger: match.trigger,
    query: before.slice(match.index + 1),
    startNode: node,
    startOffset: match.index,
  };
};
